import { FocusEvent, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { SelectInstance } from "react-select";
import { v1 } from "uuid";
import { AppConfigContext } from "../../../generic";
import { Dropdown } from "../../../ui";
import { Input } from "../Input";
import CalendarButton from "./CalendarButton";
import { DateTimeRangeCard, DateTimeRangeCardProps } from "./DateTimeRangeCard";
import { DateTimeRange } from "./DateTimeRangeType";
import { formatDateTimeRangeValue } from "./formatDateTimeRangeValue";

import styles from "./style.module.scss";


export type DateTimeRangeFieldProps = DateTimeRangeCardProps & {
  onFocus?: (e?: any) => void,
  onBlur?: (e?: any) => void,
  placeholder?: string,
  disabled?: boolean,
}

const DateTimeRangeField = memo<DateTimeRangeFieldProps>((
  {
    onFocus,
    onBlur,
    placeholder,
    value,
    onChange,
    disabled,
    ...restProps
  }) => {
  const innerId = useMemo(v1, []);

  const selectRef = useRef<SelectInstance<any, boolean, any>>();
  const [ wrappedValue, changeWrappedValue ] = useState<DateTimeRange | null | undefined>(value);
  useEffect(() => {
    changeWrappedValue(value);
  }, [ changeWrappedValue, value ]);


  const [ open, setOpen ] = useState(false);
  const close = useCallback(() => {
    setOpen(false);
    if (open) onChange?.(wrappedValue);
  }, [ onChange, wrappedValue, setOpen, open ]);
  const toggleState = useCallback(() => {
    if (open) {
      close();
    } else {
      setOpen(true);
    }
  }, [ close, setOpen, open ]);
  const wrappedOnBlur = useCallback((e: FocusEvent<HTMLInputElement>) => {
    if (onBlur) {
      onBlur(e);
    }
    close();
  }, [ close, onBlur ]);

  const [ referenceElement, setReferenceElement ] = useState(null);


  const onKeyDown = useCallback((e: any) => {
    if (e.key === "Escape") {
      if (e.defaultPrevented === false) {
        close();
      } else if ((selectRef?.current as any)?.state?.menuIsOpen === false && e.target === (selectRef?.current as any)?.select?.inputRef) {
        close();
      }
    }
  }, [ selectRef, close ]);
  const onClear = useCallback(() => {
    flushSync(() => {
      changeWrappedValue(null);
      setOpen(false);
    });
    onChange?.(null);
  }, [ setOpen, onChange ]);

  const appConfig = useContext(AppConfigContext);
  const dateFormat = restProps.dateFormat || appConfig.dataFormatMoment;
  const dateTimeFormat = restProps.dateTimeFormat || appConfig.dataTimeFormatMoment;
  const timeZone = restProps.timeZone || appConfig.timeZone;

  const displayedValue = useMemo(
    () => formatDateTimeRangeValue(value, dateFormat, dateTimeFormat, timeZone, appConfig.serverTimeZone, appConfig.serverDateTimeFormatMoment),
    [ value, dateFormat, dateTimeFormat, timeZone, appConfig.serverTimeZone, appConfig.serverDateTimeFormatMoment ],
  );

  const additionalOnBlurCheck = useCallback((e: FocusEvent<HTMLDivElement>) => {
    if (e.relatedTarget) {
      if (
        document.getElementById(innerId)
          ?.contains(e.relatedTarget as Node) ||
        (e.relatedTarget as HTMLElement).className.includes("react-datepicker")
      ) {
        return false;
      }
    }
    if (e.target) {
      if (e.target.tagName.toLowerCase() !== "input" && e.target.className.includes("react-datepicker")) {
        return false;
      }
    }
    return !e.defaultPrevented;
  }, [ innerId ]);

  return (
    <Input
      component={"input"}
      value={displayedValue || ""}
      tabIndex={-1}
      containerProps={{
        onBlur: wrappedOnBlur,
        onKeyDown,
        onFocus,
        ref: setReferenceElement,
      }}
      focused={open}
      disabled={disabled}
      onClear={onClear}
      placeholder={placeholder}
      onFocus={close}
      additionalOnBlurCheck={additionalOnBlurCheck}
      endAdornment={
        <>
          <CalendarButton disabled={disabled} onClick={toggleState} />
          <Dropdown
            popperOptions={{
              placement: "bottom-start",
              modifiers: [
                {
                  name: "flip",
                  options: {
                    fallbackPlacements: [ "bottom-end", "top-start", "top-end", "right", "left" ],
                  },
                },
              ],
            }}
            closeOnClick="never"
            id={innerId}
            isOpen={open}
            referenceElement={referenceElement}
            onRequestClose={close}
            className={styles.dropdown}
          >
            <DateTimeRangeCard
              selectRef={selectRef}
              value={wrappedValue}
              onChange={useCallback((v: DateTimeRange | null | undefined) => flushSync(() => changeWrappedValue(v)), [])}
              {...restProps} />
          </Dropdown>
        </>}
    />
  );
});

export default DateTimeRangeField;
